import React, { useMemo, useState } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { AddCrmBillDialog } from './components/add-crm-bill-dialog';
import { AccountingDocumentWithBranchNameEntity } from '../../../../../../api/types/crm/bills/crm-bills-entity';

interface AccountBillsProps {
  accountId: number | null;
  bills: AccountingDocumentWithBranchNameEntity[];
  loading: boolean;
  onRefresh: () => void;
  /** Default «לכבוד» on the receipt — typically the CRM account display name */
  defaultReceiptRecipientName?: string;
}

export const AccountBills: React.FC<AccountBillsProps> = ({
  accountId,
  bills,
  loading,
  onRefresh,
  defaultReceiptRecipientName = '',
}) => {
  const [showAddDialog, setShowAddDialog] = useState<boolean>(false);

  const sortedBills = useMemo(() => {
    const copy = [...(bills ?? [])];
    copy.sort((a, b) => b.id - a.id);
    return copy;
  }, [bills]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ fontSize: '1.1rem', fontWeight: 700 }}>חשבוניות</div>
        <Button
          label="הוסת חשבון"
          icon="pi pi-plus"
          onClick={() => setShowAddDialog(true)}
          disabled={!accountId}
        />
      </div>

      <DataTable
        value={sortedBills}
        loading={loading}
        dataKey="id"
        emptyMessage={
          <div style={{ textAlign: 'right' }}>לא נמצאו חשבוניות</div>
        }
        style={{ direction: 'rtl' }}
      >
        <Column
          field="branchName"
          header="שלוחה"
          style={{ textAlign: 'right' }}
          body={(row: AccountingDocumentWithBranchNameEntity) =>
            row.branchName?.trim() ? row.branchName : 'לא קיים'
          }
        />
        <Column
          field="id"
          header="מזהה"
          style={{ width: '90px', textAlign: 'right' }}
        />
        <Column
          field="docType"
          header="סוג מסמך"
          style={{ textAlign: 'right' }}
        />
        <Column
          field="docNumber"
          header="מס׳ מסמך"
          style={{ textAlign: 'right' }}
        />
        <Column
          field="amount"
          header="סכום"
          style={{ textAlign: 'right' }}
          body={(row: AccountingDocumentWithBranchNameEntity) =>
            typeof row.amount === 'number' ? row.amount.toFixed(2) : 'חסר'
          }
        />
        <Column
          header="מסמך"
          style={{ width: '200px', textAlign: 'right' }}
          body={(row: AccountingDocumentWithBranchNameEntity) => {
            const fileUrl = (row as any).fileUrl as string | null | undefined;
            if (!fileUrl) {
              return <span style={{ color: '#6B7280' }}>לא מצורף מסמך</span>;
            }
            return (
              <button
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  padding: '0.5rem 1rem',
                  border: '1px solid #E5E7EB',
                  borderRadius: '0.375rem',
                  backgroundColor: 'white',
                  color: '#374151',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  cursor: 'pointer',
                  boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  window.open(fileUrl, '_blank');
                }}
              >
                <i
                  className="pi pi-download"
                  style={{ fontSize: '1rem', marginRight: '0.5rem' }}
                />
                לחץ לצפייה
              </button>
            );
          }}
        />
      </DataTable>

      <AddCrmBillDialog
        visible={showAddDialog}
        accountId={accountId}
        onHide={() => setShowAddDialog(false)}
        onSuccess={onRefresh}
        defaultReceiptRecipientName={defaultReceiptRecipientName}
      />
    </div>
  );
};
